<template>
    <div class="signal-item">
        <div class="signal-item--form">
            <Select
                v-model="signalFormType"
                class="signal-item--type-select"
                data-testid="connect__signal-type-select"
                label="Select trigger"
            >
                <Option value="DATETIME">Date and time</Option>
                <Option value="WEATHER">Weather</Option>
            </Select>

            <DatetimeSignalForm
                v-if="signalFormType === 'DATETIME'"
                :model-value="dateFormModelValue.signalValue"
                @update:modelValue="updateDateValue"
            />

            <WeatherSignalForm
                v-else-if="signalFormType === 'WEATHER'"
                :model-value="weatherFormModelValue"
                @update:modelValue="updateWeatherValue"
            />
        </div>

        <div class="signal-item--delete-button" data-testid="connect__signal-delete-button" @click="emit('delete')">
            <Icon v-if="signalFormType" type="md-trash" />
            <Icon v-else type="md-close" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, type PropType } from "vue";

import type { SignalForm } from "@/Connect/components/Accounts/PlatformAccountRuleSignal.vue";
import DatetimeSignalForm from "@/Connect/components/Accounts/PlatformAccountRuleSignalFormDate.vue";
import WeatherSignalForm from "@/Connect/components/Accounts/PlatformAccountRuleSignalFormWeather.vue";

const DEFAULT_DATE_FORM = { signalType: "DATETIME", signalValue: "" };
const DEFAULT_WEATHER_FORM = { signalType: "OPEN_WEATHER_TEMPERATURE", signalValue: "" };

function formTypeFromSignalType(signalType: string | undefined): string {
    switch (signalType) {
        case "OPEN_WEATHER_TEMPERATURE":
        case "OPEN_WEATHER_RAIN":
        case "OPEN_WEATHER_SNOW":
            return "WEATHER";
        case "DATETIME":
            return "DATETIME";
        default:
            return "";
    }
}

const { modelValue } = defineProps({
    modelValue: {
        type: Object as PropType<SignalForm>,
        required: true
    }
});

const emit = defineEmits(["update", "delete"]);

const signalFormType = ref(formTypeFromSignalType(modelValue.signalType));
const dateFormModelValue = ref(signalFormType.value === "DATETIME" ? modelValue : DEFAULT_DATE_FORM);
const weatherFormModelValue = ref(signalFormType.value === "WEATHER" ? modelValue : DEFAULT_WEATHER_FORM);

function updateDateValue(value: string) {
    dateFormModelValue.value.signalValue = value;
    emit("update", dateFormModelValue.value);
}

function updateWeatherValue(value: SignalForm) {
    weatherFormModelValue.value = value;
    emit("update", weatherFormModelValue.value);
}

watch(signalFormType, (newValue, oldValue) => {
    if (newValue === oldValue) {
        return;
    }

    if (newValue === "DATETIME") {
        emit("update", dateFormModelValue.value);
    } else {
        emit("update", weatherFormModelValue.value);
    }
});
</script>

<style scoped lang="scss">
.signal-item {
    position: relative;

    border-radius: 8px;

    border: 1px solid var(--wpp-grey-color-400);
    background: var(--Grey-000, #fff);

    width: 100%;

    display: flex;
    justify-content: space-between;
}

.signal-item--form {
    flex: 1;

    padding: 16px;
}

.signal-item--type-select {
    width: 150px;
}

.signal-item--delete-button {
    border-left: 1px solid var(--wpp-grey-color-400);

    padding: 20px 10px;
    display: flex;
    align-items: center;

    cursor: pointer;
}
</style>
